import request from './request'
import { IResponseList, IListReq, REQUEST_METHOD, ExpandRecursively } from '@da-vinci/utils'
import { LocaleType } from '@mogic/dv-design'
import { UserChannelType, SourceChannel, VideoType } from '@da-vinci/shared'
import { trimObjLight } from '@/utils'
import { PromptValue } from '@/components/VideoList'
import { NormalSortOptType } from '@/enums'

// 白名单校验
export function getCheckWhiteList(data: { account: string }) {
  return request<boolean>({
    method: REQUEST_METHOD.GET,
    url: '/account/checkWhiteList',
    data: trimObjLight(data),
  })
}

// 发送验证码
export function sendCaptcha(data: { account: string; sendType: number; slideCode: number; slideToken: string }) {
  return request<null>(
    {
      method: REQUEST_METHOD.POST,
      url: '/account/sendCaptcha',
      data: trimObjLight(data),
    },
    { ignoreMessage: true }
  )
}

// 获取滑动验证码
export interface SlidePicCode {
  original: string
  slide: string
  slideToken: string
}
export function getSlidePicCodeApi() {
  return request<SlidePicCode>({
    method: REQUEST_METHOD.GET,
    url: '/account/getSlidePicCode',
  })
}

// 验证码登录
export interface UserInfoReq {
  userId: string
  mobile: string
  token: string
  firstLogin: string
}
export function loginByVerifyCode(data: { account: string; verifyCode: string }) {
  return request<UserInfoReq>({
    method: REQUEST_METHOD.POST,
    url: '/account/loginByVerifyCode',
    data: trimObjLight(data),
  })
}

// 设置密码
export function resetPasswordNotSMS(data: { userId: string; password: string; repassword: string }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/account/resetPasswordNotSMS',
    data: trimObjLight(data),
  })
}

// 密码登录
export function loginByPassword(data: { account: string; password: string }) {
  return request<UserInfoReq>({
    method: REQUEST_METHOD.POST,
    url: '/account/loginByPassword',
    data: trimObjLight(data),
  })
}

// 忘记密码
export function forgotPassword(data: { account: string; password: string; captcha: string }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/account/forgotPassword',
    data: trimObjLight(data),
  })
}

// 修改个人信息
export function updateAccountInfo(data: {
  userId: string
  platformAvatar?: string
  username: string
  avatarColor?: string
}) {
  let changeAvatarType
  if (data.platformAvatar) {
    // 上传图片头像
    changeAvatarType = 1
  } else if (data.avatarColor) {
    // 更改颜色
    changeAvatarType = 2
  }
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/account/updateAccountInfo',
    data: {
      changeAvatarType,
      ...trimObjLight(data),
    },
  })
}

// 退出登录
export function logout(data: { token: string }) {
  return request<boolean>(
    {
      method: REQUEST_METHOD.POST,
      url: '/account/logout',
      data: trimObjLight(data),
    },
    { ignoreMessage: true }
  )
}

//  查询用户信息（包含洞察列表查询）
export interface OrgInfo {
  orgName: string
  hasConfirmed: boolean
  orgHeadImg: string
  hasPushed: boolean
  orgId: string
}
export interface TenantInfo {
  tenantId: string
  tenantName: string
  tenantAvatar: string
  amount?: number
}
export interface UserInfo {
  userId?: string
  nickname?: string
  username?: string
  avatarColor?: string
  platformAvatar?: string
  hasSetPassword?: boolean
  hasSetAccountName?: boolean
  orgList?: OrgInfo[]
  tenantInfo?: TenantInfo[]
}
export function queryUserInfo() {
  return request<UserInfo>({
    method: REQUEST_METHOD.POST,
    url: '/account/queryUserInfo',
  })
}

export const RequestTypeNameMap = {
  video: '视频整体',
  clip: '画面',
  oral: '口播',
}
export type RequestType = keyof typeof RequestTypeNameMap
export interface BrandItem {
  name: string
  focus: boolean
}
// 洞察列表组内数据查询
export interface VideoListItem {
  video5sFile: string
  unlock: boolean
  recommendType?: RecommendType
  md5: string
  videoId: string
  custId: string
  orderId: string
  kolId: string
  kolType: string
  kolName: string
  kolHeadImg: string
  kolPageUrl: string
  sourceChannel: SourceChannel

  title: string
  videoRelateTopic?: string[] // 视频话题
  brandName?: BrandItem[]
  tags?: Tag[]
  channel: UserChannelType
  videoType: VideoType
  publishDate: string
  duration: string
  category: string
  categoryId: number
  mediaType: string
  coverImgPath: string
  coverImgWidth: number
  coverImgHeight: number
  frameRate: number
  processUrl: string
  originalUrl: string
  resolution: string
  likeCount: number //点赞
  repostCount: number //转发
  commentCount: number //评论
  favourateCount: number //收藏
  showCount?: number //曝光数

  mediaStructure?: string // 视频结构
  targetAudience?: string // 推测受众
  psychologyAnalysis?: string // 心理分析

  clipId?: number // 画面 id
  clipUrl?: string //画面封面图
  semanticResult?: string // 画面描述
  oralTextId?: number //口播id
  oralCoverUrl?: string //口播封面图
  asrText?: string // 口播文案

  startTs?: number //开始秒
  endTs?: number //结束
  indexNum?: number // 第几个片段

  highlight?: Record<string, string[]>
  storylineIndex?: number
  storylineId?: string
  tagAttr?: string[]
}

export type PageQueryVideoListReq = Omit<PromptValue, 'requestType' | 'recommend' | 'industryTrend'> & {
  requestType?: RequestType
  recommendType: RecommendType[]
}
export function pageQueryVideoList(data: ExpandRecursively<IListReq & PageQueryVideoListReq>) {
  return request<IResponseList<VideoListItem>>({
    method: REQUEST_METHOD.POST,
    url: '/insight/pageQueryVideoList',
    data,
  })
}

interface GlobalAnalysis {
  mediaStructure: string
  targetAudience: string[]
  psychologyAnalysis: string
}
export const AnalysisNameMap: Record<keyof GlobalAnalysis, string> = {
  mediaStructure: '视频结构',
  targetAudience: '推测受众',
  psychologyAnalysis: '心理分析',
}

export const LanguageLocaleMap = {
  Chinese: LocaleType.zhCN,
  English: LocaleType.en,
} as const satisfies Record<string, LocaleType>
export type Language = keyof typeof LanguageLocaleMap
export type RecommendType = 'recommend' | 'industryTrend'
export interface IVideoInfo {
  recommendType?: RecommendType
  collectionVideo: boolean
  channel: UserChannelType
  videoId: string
  kolId: string
  recommendVideo?: boolean
  kolName: string
  kolHeadImg?: string
  kolPageUrl?: string
  videoType: VideoType
  md5: string
  duration: number
  coverImgWidth: number
  coverImgHeight: number
  frameRate: number
  coverImgPath: string
  resolution: string
  videoTitle: string
  name: string
  videoRelateTopic?: string[]
  likeCount?: string
  commentCount?: string
  collectCount?: string
  forwardCount?: string
  detailUrl: string
  processUrl: string
  originalUrl: string
  publishDate: string
  brandName?: BrandItem[]
  language: Language
  sourceChannel: SourceChannel
  landingType?: string // 推广目标
  industryName?: string // 投放行业
  allTitle?: string // 全部标题
  extend?: {
    ctr: string //点击率
    conversionRate: string //转化率
    convertShowRate: string //曝光转化率
    totalPlay: string //热度
    playOverRate: string //播放完播率
    playOverRate3s: string //3秒播放完播率
    showCnt: string //曝光数
    clickCnt: string //点击数
    followCnt: string //关注量
    forwardExpressive: string //转发表现
    commentExpressive: string //评论表现
    likeExpressive: string //点赞表现
  }

  status:
    | 'await'
    | 'processing'
    | 'retry_processing'
    | 'process_failed'
    | 'completed'
    | 'pre_completed'
    | 'base_completed'
}
interface Tag {
  id: string
  bizId: string
  bizType: string
  tagValue: string
  tagValueId: string
  tagType: string
  tagAttr: string
}

// 提交单条任务分析任务
export function commitTask(data: { url: string; md5?: string; videoTitle?: string }) {
  return request<boolean>(
    {
      method: REQUEST_METHOD.POST,
      url: '/commitTask',
      data,
    },
    {
      returnNativeResponse: true,
      timeout: 30 * 1000,
    }
  )
}

// 轮询任务状态及结果
export interface AnalysisTask {
  taskId: string
  status: 'doing' | 'finish' | 'fail' | 'await' | 'init'
  videoMd5: string
  videoUrl: string
  videoImageUrl: string
  channel: UserChannelType
  videoTitle: string
  gmtCreate: string
  unlockVideo?: boolean
}
export function queryTaskDetail(data: { taskIds: string[] }) {
  return request<IResponseList<AnalysisTask>>({
    url: '/queryTaskDetail',
    method: REQUEST_METHOD.POST,
    data,
  })
}

// 获取任务列表
export function queryTaskList(data: IListReq) {
  return request<IResponseList<AnalysisTask>>({
    url: '/queryTaskList',
    method: REQUEST_METHOD.POST,
    data,
  })
}

export function getOssAccessKey() {
  return request(
    {
      url: '/aliyun/oss/assumeRole',
      method: 'get',
    },
    { sysPrefix: '/openai' }
  )
}

export enum BriefStatus {
  doing = 'await', // 待处理
  scripCopy = 'scripCopy', // 文案仿写
  sceneCaption = 'sceneCaption', // 画面描述/景别
  sceneRef = 'sceneRef', // 画面参考
  sceneImage = 'sceneImage', // 简笔画
  done = 'completed',
  fail = 'fail',
}
export interface ISingleVideoInfo {
  username: string
  avatarColor: string
  gmtCreate: string
}
export interface IBriefDetail {
  briefId: string
  brand: string
  projectId: string
  projectName: string
  itemName: string
  sellingPoint: string
  videoTemplateMd5: string
  oralRequire: string
  imageRequire: string
  otherRequire: string
  gmtCreate: string | number
  status: BriefStatus
  aiBrainBriefScriptResponseList?: IBriefScriptInfo[]
  sceneUrl?: string
  videoInfo: IVideoInfo
  singleVideoInfo?: ISingleVideoInfo
  title: string
  projectDelStatus: 0 | 1 // 1 项目已删除
  reGenerateType?: BriefRGenerateType
  generateProject?: boolean
  totalDurationMs?: number
  subItemInfo?: IItemSubItemInfo[]
}
type BriefRGenerateType = 'oral' | 'oralAndImage' | 'image' | 'originalOralImage'
export interface IBriefScriptInfo {
  endOffset: number
  startOffset: number
  storylineIndex: number
  id?: string
  caption?: string
  sceneType?: string[]
  oralText?: string
  oralTextEm?: string
  originalOral?: string
  oral?: string[]
  oralRequire?: string
  remark?: string
  sceneUrl?: string
  duration?: string
  materialDesc?: string
  clipIndex: number
  status: BriefStatus
  sceneTag?: string[]
  briefSceneRefResponse?: IBriefSceneRefResponse
  extendResponseList?: IBriefSceneRefResponse[]
  originalOralResponseList?: IBriefSceneRefResponse[]
}
export type IBriefSceneRefResponse = {
  channel: UserChannelType
  segmentId: string
  segmentIndex: number
  caption?: string
  kolId?: string
  kolName?: string
  kolHeadImg?: string
  kolPageUrl?: string
  videoType: VideoType
  likeCount: string
  commentCount: string
  favourateCount: string
  repostCount: string
  sourceChannel: SourceChannel
  showCount: number //曝光数
  brand?: string[]
  md5: string
  storylineId: string
  storylineIndex: number
  coverImgPath: string
  title: string
  tagAttr: string[]
  processUrl: string
  startTs: number
  endTs: number
  coverImgHeight: number
  coverImgWidth: number
  projectMaterialId?: string

  useCount?: number
} & Pick<
  VideoListItem,
  'likeCount' | 'commentCount' | 'favourateCount' | 'repostCount' | 'sourceChannel' | 'channel' | 'showCount'
>

// 查询创意详情
export function queryBriefDetail(data: { briefId: string }) {
  return request<IBriefDetail>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryBriefTask',
    data,
  })
}

// 删除brief
export function deleteBrief(data: { briefId: string }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/brief/deleteBrief',
    data,
  })
}

export interface IQueryBriefPageFilter {
  title: string
  sort: NormalSortOptType
  itemId: string[]
  projectId: string[]
}
//获取brief列表
export function queryBriefList(data: IListReq<IQueryBriefPageFilter>) {
  return request<IResponseList<IBriefDetail>>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryBriefPage',
    data,
  })
}

export interface BriefFormState {
  brand?: string // 品牌名称
  itemName?: string // 商品名称
  sellingPoint?: string // 商品卖点
  videoTemplateMd5?: string // 参考视频
  oralRequire?: string // 口播要求
  projectId?: string // 项目id
  projectName?: string
  updateItem?: boolean
}
// 提交创意
export function commitBrief(data: BriefFormState) {
  return request<{ briefId: string }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/commitBrief',
    data,
  })
}

// 编辑创意
export function editBrief(
  data: Partial<BriefFormState> & {
    briefId: string
    reGenerate: boolean
    reGenerateStorylineIndex?: number[]
    oralResourceMd5?: string
    reGenerateType?: BriefRGenerateType
  }
) {
  return request<{ briefId: string }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/editBrief',
    data,
  })
}

// 删除脚本
export function deleteBriefScript(data: { briefId: string; storylineIndex: number }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/brief/deleteBriefScript',
    data,
  })
}

// 编辑脚本
export interface EditScriptReq {
  briefId: string
  storylineIndex?: number
  scriptId?: string
  oral?: string[]
  oralRequire?: string
  caption?: string
  reGenerateType?: 'oral'
  reGenerate?: boolean
}
export function editScript(data: EditScriptReq) {
  return request<{ briefId: string; scriptId: string }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/editScript',
    data,
  })
}

// 查询brief脚本详情
export function queryScriptDetail(data: { briefId: string; scriptId: string }) {
  return request<IBriefScriptInfo>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryScriptDetail',
    data,
  })
}

// 重新生成brief
export function reGenerateBrief(data: { briefId: string }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/brief/reGenerate',
    data,
  })
}

// 获取brief助理会话列表
export interface IBriefAssistant {
  id?: string
  brand?: string
  itemName?: string
  oralRequire?: string
  sellingPoint?: string
  projectId?: string
  projectName?: string
  briefId?: string
  type: 'text' | 'form' | 'info' | 'optimizationTips' | 'optimizationOk'
  role: 'user' | 'robot'
  sessionText: string
  gmtCreate?: string
  subItemInfo?: string // JSON IItemSubItemInfo[]
}
export function queryBriefAssistant(data: { briefId: string }) {
  return request<IBriefAssistant[]>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryBriefAssistant',
    data: trimObjLight(data),
  })
}

// 创意助理交互提交
export function briefAssistantCommit(data: IBriefAssistant) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/brief/briefAssistantCommit',
    data: trimObjLight(data),
  })
}

// 创建副本
export function copyBrief(data: { briefId: string; title: string }) {
  return request<{ briefId: string }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/copyBrief',
    data: trimObjLight(data),
  })
}

// 重命名
export function renameBrief(data: { briefId: string; title: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/brief/rename',
    data: trimObjLight(data),
  })
}

// 导出
export function exportBrief(data: { briefId: string }) {
  return request<string>({
    method: REQUEST_METHOD.POST,
    url: '/brief/export',
    data: trimObjLight(data),
  })
}

// 导出任务查询
export function queryExportAsyncTask(data: { id: string }) {
  return request<IExportRecord>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryExportAsyncTask',
    data: trimObjLight(data),
  })
}

/* 编辑商品 */
export type IItemInfo = Required<
  Pick<IItemDetail, 'brandName' | 'itemName' | 'sellingPoint' | 'usePackageItem' | 'subItemInfo'>
>
export function editItem(data: IItemInfo & { id: string; delSubItemIds?: string[] }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/item/editItem',
    data: trimObjLight(data),
  })
}

// 添加商品
export function addItem(data: IItemInfo) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/item/addItem',
    data: trimObjLight(data),
  })
}

// 删除商品
export function deleteItem(data: { id: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/item/deleteItem',
    data: trimObjLight(data),
  })
}

// 获取商品列表
export interface IItemDetail {
  id: string
  tenantId: string
  brandName: string
  itemName: string
  sellingPoint: string
  gmtCreate: string
  gmtModify: string
  modifyId: string
  modifier: string
  createId: string
  creator: string
  usePackageItem?: boolean // 开启使用套装商品
  subItemInfo?: IItemSubItemInfo[]
}
export interface IItemSubItemInfo {
  id?: string
  brandName?: string
  itemName?: string
  sellingPoint?: string
  coverImageUrl?: string
}
export interface IQueryItemPageFilter {
  itemKeyword?: string
  brandName?: string[]
  createId?: string[]
  sort: NormalSortOptType
}
export function queryItemPage(data: IListReq<IQueryItemPageFilter>) {
  return request<IResponseList<IItemDetail>>({
    method: REQUEST_METHOD.POST,
    url: '/item/queryItemPage',
    data: trimObjLight(data),
  })
}

// 商品详情
export function queryItemDetail(data: { itemId: string }) {
  return request<IItemDetail>({
    method: REQUEST_METHOD.POST,
    url: '/item/queryItemDetail',
    data: trimObjLight(data),
  })
}

// 素材删除（只删除项目素材关联关系）
export function deleteMaterial(data: { projectId: string; ids: string[] }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/material/deleteMaterial',
    data: trimObjLight(data),
  })
}

// 素材重命名
export function renameMaterial(data: { projectId: string; id: string; resourceName: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/material/rename',
    data: trimObjLight(data),
  })
}

export interface IProjectMaterial {
  id: string
  projectId: string
  coverImgHeight: number
  coverImgPath: string
  coverImgWidth: number
  duration: number
  frameRate: number
  mediaType: string
  name: string
  originalUrl: string
  processUrl: string
  resolution: string
  resourceId: number
  splitStatus: number
  status: number
  summaryImgHeight: number
  summaryImgPath: string
  summaryImgWidth: number
  summaryPerFrmHeight: number
  summaryPerFrmWidth: number
  uploadFileSize: string
  uploadFileSubType: string
  uploadTime: string
  gmtCreate: string
  gmtModify: string
  createId: string
  creator: string
  resourceMd5: string
}
// 素材分页列表
export interface IQueryPageMaterialFilter {
  keyword: string
  createId: string[]
  sort: string
}
export function queryPageMaterial(data: IListReq<IQueryPageMaterialFilter & { projectId: string }>) {
  return request<IResponseList<IProjectMaterial>>({
    method: REQUEST_METHOD.POST,
    url: '/material/queryPageMaterial',
    data: trimObjLight(data),
  })
}

// 查询素材品页面下拉选项
export interface ICreatorInfo {
  createId: string
  creator: string
}
export function queryMaterialSearch(data: { projectId: string }) {
  return request<{
    creatorInfo: ICreatorInfo[]
  }>({
    method: REQUEST_METHOD.POST,
    url: '/material/queryMaterialSearch',
    data: trimObjLight(data),
  })
}

// 素材管理 - 添加素材
export interface IAddMaterialReq {
  ossUrl?: string
  resourceMd5: string
  resourceName: string
  mediaType: string
  uploadFileSize: number
  uploadFileSubType: string
  uploadFileType: string
  duration?: number
}
export function addMaterial(data: IAddMaterialReq & { projectId: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/material/addMaterial',
    data,
  })
}

// 上传素材校验是否已经存在
export function checkExist(data: { projectId: string; resourceMd5List: string[] }) {
  return request<{
    projectExistMd5List: string[]
    materialExistMd5List: string[]
  }>({
    method: REQUEST_METHOD.POST,
    url: '/material/checkExist',
    data: trimObjLight(data),
  })
}

// 查询页面下拉选项(修改)
export interface IProjectOption {
  projectId: string
  itemName?: string
  brandName?: string
  projectName?: string
  sellingPoint?: string
  totalDurationMs?: number
  subItemInfo?: IItemSubItemInfo[]
}
export interface IItemOption {
  itemId: string
  brandName: string
  sellingPoint: string
  itemName: string
}
export function queryBriefSearch() {
  return request<{
    projectInfo: IProjectOption[]
    itemInfo: IItemOption[]
  }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryBriefSearch',
  })
}

// 查询商品页面下拉选项
export interface IItemSearchOpts {
  brandName: string[]
  creatorInfo: ICreatorInfo[]
}
export function queryItemSearch() {
  return request<IItemSearchOpts>({
    method: REQUEST_METHOD.POST,
    url: '/item/queryItemSearch',
  })
}

// 查询项目页面下拉选项
export interface IProjectSearchOpts {
  itemInfo: IItemOption[]
  creatorInfo: ICreatorInfo[]
}
export function queryProjectSearch() {
  return request<IProjectSearchOpts>({
    method: REQUEST_METHOD.POST,
    url: '/project/queryProjectSearch',
  })
}

export interface IProjectDetail {
  id: string
  itemId: string
  projectName: string
  tenantId: string
  brandName: string
  itemName: string
  sellingPoint: string
  materialCount: number
  gmtCreate: string
  gmtModify: string
  modifyId: string
  modifier: string
  createId: string
  creator: string
  totalDurationMs: number
  subItemInfo?: IItemSubItemInfo[]
}
// 获取项目列表
export interface IQueryProjectPageFilter {
  keyword?: string
  itemId?: string[]
  createId?: string[]
  sort: NormalSortOptType
}
export function queryProjectPage(data: IListReq<IQueryProjectPageFilter>) {
  return request<IResponseList<IProjectDetail>>({
    method: REQUEST_METHOD.POST,
    url: '/project/queryProjectPage',
    data: trimObjLight(data),
  })
}

// 添加项目
export function createProject(data: { projectName: string; itemId: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/project/createProject',
    data: trimObjLight(data),
  })
}

// 编辑项目
export function editProject(data: { projectId: string; projectName: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/project/editProject',
    data: trimObjLight(data),
  })
}

// 删除项目
export function deleteProject(data: { projectId: string }) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/project/deleteProject',
    data: trimObjLight(data),
  })
}

// 替换素材
export interface ReplaceMaterialReq {
  briefId: string
  scriptId: string
  resourceMd5: string
  resourceUrl: string
  segmentId?: string
  startOffset?: number
  endOffset?: number
  coverImgPath?: string
  replaceIndex?: number
}
export function replaceMaterial(data: ReplaceMaterialReq) {
  return request({
    method: REQUEST_METHOD.POST,
    url: '/brief/replaceMaterial',
    data: trimObjLight(data),
  })
}

// 替换画面查询
export interface IQueryMaterialReq {
  briefId: string
  scriptId: string
  searchType: 'explore' | 'segment'
  materialDesc?: string
}
export function queryMaterial(data: IQueryMaterialReq) {
  return request<{
    briefSceneRefResponses: IBriefSceneRefResponse[]
    processNum: string | number
  }>(
    {
      method: REQUEST_METHOD.POST,
      url: '/brief/queryMaterial',
      data: trimObjLight(data),
    },
    { timeout: 20 * 1000 }
  )
}

// 导出列表
export interface IExportRecord {
  id: string
  taskId: string
  status: 'doing' | 'fail' | 'completed'
  briefOssUrl: string
  briefImageUrl: string
  briefTitle: string
  gmtModify: string
}
export function queryExportPage(data: IListReq & { ids?: string[] }) {
  return request<IResponseList<IExportRecord>>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryExportPage',
    data: trimObjLight(data),
  })
}

// 素材详情
export interface IMaterialSegment {
  id: string
  segmentType: string
  segmentId: string
  caption: string
  resourceMd5: string
  resourceId: string
  proStatus: string
  url: string
  errorMsg: string
  gmtReferStart: string
  gmtReferEnd: string
  duration: number
  coverImgPath: string
  coverImgWidth: number
  coverImgHeight: number
  sort: number
  gmtCreate: string
  gmtModify: string

  useCount?: number
}
export interface IMaterialDetail {
  id: string
  projectId: string
  coverImgHeight: number
  coverImgPath: string
  coverImgWidth: number
  cutParameter: string
  description: string
  duration: number
  frameRate: number
  gmtReferEnd: number
  gmtReferStart: number
  mediaType: string
  name: string
  originalUrl: string
  processUrl: string
  rate: number
  resolution: string
  resource: string
  resourceId: string
  segmentId: string
  splitStatus: number
  status: number
  summaryImgHeight: number
  summaryImgPath: string
  summaryImgWidth: number
  summaryPerFrmHeight: number
  summaryPerFrmWidth: number
  uploadFileSize: string
  uploadFileSubType: string
  uploadTime: string
  materialSegmentList?: IMaterialSegment[]
  gmtCreate: string
  gmtModify: string
  createId: string
  creator: string
  qualityLabelStatus?: number
}
export function queryMaterialDetail(data: { projectId: string; id: string }) {
  return request<IMaterialDetail>({
    method: REQUEST_METHOD.POST,
    url: '/material/queryMaterialDetail',
    data: trimObjLight(data),
  })
}

// 项目详情
export function queryProjectDetail(data: { projectId: string }) {
  return request<IProjectDetail>({
    method: REQUEST_METHOD.POST,
    url: '/project/queryProjectDetail',
    data: trimObjLight(data),
  })
}

// 重试导出
export function reExportBrief(data: { id: string }) {
  return request(
    {
      method: REQUEST_METHOD.POST,
      url: '/brief/reExport',
      data,
    },
    { returnNativeResponse: true }
  )
}

export interface InsightDetailRes {
  videoInfo: IVideoInfo
  globalAnalysis: GlobalAnalysis
  storyline?: StoryLine[]
  singleVideoInfo?: ISingleVideoInfo
}
export interface StoryLine {
  id: string
  name: string
  gmtStart: number
  gmtEnd: number
  storyPoint: string
  storyText: string
  coverImgHeight: number
  coverImgPath: string
  coverImgWidth: number
  brandName?: BrandItem[]
  tags?: Tag[]
  videoShotList: VideoShot[]
  textShotList: VideoShot[]
}

interface Tag {
  id: string
  bizId: string
  bizType: string
  tagValue: string
  tagValueId: string
  tagType: string
  tagAttr: string
}

interface VideoShot {
  startOffset: number
  endOffset: number
  duration: number
  coverImgHeight: number
  coverImgWidth: number
  coverImgPath: string
  semanticResult?: string
  text?: string
  tags?: Tag[]
  indexNum: number
  id: string
}
// 视频详情
export function queryInsightDetail(data: { md5: string }) {
  return request<InsightDetailRes>({
    method: REQUEST_METHOD.POST,
    url: '/insight/detail',
    data: trimObjLight(data),
  })
}

export function keywordDetection(data: { content: { text: string }[] }) {
  return request<{ text: string; words: { position: [number, number] }[] }[]>(
    {
      method: REQUEST_METHOD.POST,
      url: '/keyword_detection',
      data: {
        ...data,
        category: 'ALL_CATEGORY',
        channel: 'ALL_CHANNEL',
        detection_mode: 0,
        detection_type: 2,
        keywords: [],
        source: 'video_brain',
        filter_text_by_hit: 1,
      },
    },
    { apiUrl: 'https://dev-keyword-detect-service.getmogic.com', sysPrefix: '', returnNativeResponse: true }
  )
}

// 视频解锁（新增）
export type VideoUnlockType = 'single' | 'explore'
export function videoUnlock(data: { md5: string; unlockType: VideoUnlockType }) {
  return request<boolean>({
    method: REQUEST_METHOD.POST,
    url: '/insight/videoUnlock',
    data: trimObjLight(data),
  })
}

// 查询项目下处理中的素材数量（新增）
export function countProcessMaterial(data: { projectId: string }) {
  return request<{ projectId: string; processingCount: number }>({
    method: REQUEST_METHOD.POST,
    url: '/brief/countProcessMaterial',
    data: trimObjLight(data),
  })
}

// 查询保留原声的素材
export interface IOralMaterial {
  coverImgPath: string
  resourceMd5: string
  resourceUrl: string
  startOffset: number
  endOffset: number
  segmentId: string
  projectMaterialId: string
  resourceName: string
}
export function queryOralMaterial(data: { briefId: string; projectId: string }) {
  return request<IOralMaterial[]>({
    method: REQUEST_METHOD.POST,
    url: '/brief/queryOralMaterial',
    data: trimObjLight(data),
  })
}
